/** Sprint 5-d PR-5 · 租戶 AI 設定頁（/ee_tenant_ai_config·租戶管理員）。

AI 模式啟用（功能/聊天·聊天需 can_enable_chat）+ 模式/模型/cache/heavy/預算設定 + 本月用量。
enabled/disabled_tools 進階逐工具開關 → 後續（本頁先做核心設定）。
*/
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel,
  FormLabel, Paper, Radio, RadioGroup, Stack, Switch, TextField, Typography,
} from '@mui/material';

import { useMessage } from '../../../contexts/MessageContext';
import { useChatModeAvailable } from '../../../hooks/useChatModeAvailable';
import {
  TenantAiConfig, getTenantAiConfig, getTenantAiUsage, putTenantAiConfig,
} from './services/aiChatService';

const TenantAiConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const chatModeAvailable = useChatModeAvailable();   // E4：聊天模式由訂閱決定（唯讀顯示）
  const [cfg, setCfg] = useState<TenantAiConfig | null>(null);
  const [usage, setUsage] = useState<{ period: string; total_cost: string; currency: string; monthly_budget_ntd: number | null } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setCfg(await getTenantAiConfig());
    } catch (err) { showApiError(err); }
    try { setUsage(await getTenantAiUsage() as never); } catch { /* silent ok: 用量載入失敗不阻擋設定 */ }
  }, [showApiError]);
  useEffect(() => { load(); }, [load]);

  const patch = (p: Partial<TenantAiConfig>) => setCfg((c) => (c ? { ...c, ...p } : c));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const saved = await putTenantAiConfig({
        mode: cfg.mode, default_model: cfg.default_model, escalation_model: cfg.escalation_model,
        prompt_cache_enabled: cfg.prompt_cache_enabled, allow_heavy_tools: cfg.allow_heavy_tools,
        monthly_budget_ntd: cfg.monthly_budget_ntd, budget_alert_percent: cfg.budget_alert_percent,
      });
      setCfg((c) => (c ? { ...c, ...saved } : saved));
      showSuccess('SYS020001', { name: t('aiConfig.title', 'AI 設定') });
    } catch (err) { showApiError(err); } finally { setSaving(false); }
  };

  if (!cfg) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>🤖 {t('aiConfig.title', 'AI 設定')}</Typography>

      {/* AI 模式（E4 §4.5：唯讀·聊天模式由「AI 聊天助手」訂閱決定）*/}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>{t('aiConfig.mode', 'AI 模式')}</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('aiConfig.functionMode', '功能模式（表單 GUI·一律開啟）')}
        </Typography>
        <Alert severity={chatModeAvailable ? 'success' : 'info'}>
          {chatModeAvailable
            ? t('aiConfig.chatEnabled', '聊天模式：已啟用（AI 聊天助手訂閱已核准）')
            : t('aiConfig.chatDisabled', '聊天模式：未啟用（請至服務訂閱管理申請 AI 聊天助手）')}
        </Alert>
      </Paper>

      {/* 設定 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>{t('aiConfig.settings', '對話設定')}</Typography>
        <Stack spacing={2}>
          <FormControl>
            <FormLabel>{t('aiConfig.behaviorMode', '行為模式')}</FormLabel>
            <RadioGroup row value={cfg.mode} onChange={(e) => patch({ mode: e.target.value })}>
              <FormControlLabel value="function_only" control={<Radio />} label={t('aiConfig.functionOnly', '功能模式')} />
              <FormControlLabel value="exploratory" control={<Radio />} label={t('aiConfig.exploratory', '探索模式')} />
            </RadioGroup>
          </FormControl>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('aiConfig.defaultModel', '預設模型')} size="small" fullWidth
              value={cfg.default_model} onChange={(e) => patch({ default_model: e.target.value })} />
            <TextField label={t('aiConfig.escalationModel', '升級模型')} size="small" fullWidth
              value={cfg.escalation_model} onChange={(e) => patch({ escalation_model: e.target.value })} />
          </Stack>
          <FormControlLabel control={<Switch checked={cfg.prompt_cache_enabled}
            onChange={(e) => patch({ prompt_cache_enabled: e.target.checked })} />}
            label={t('aiConfig.promptCache', '啟用 Prompt Cache（降低成本）')} />
          <FormControlLabel control={<Switch checked={cfg.allow_heavy_tools}
            onChange={(e) => patch({ allow_heavy_tools: e.target.checked })} />}
            label={t('aiConfig.allowHeavy', '允許重量級工具（跨檔搜尋等）')} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('aiConfig.monthlyBudget', '月預算 NT$')} size="small" type="number" fullWidth
              value={cfg.monthly_budget_ntd ?? ''}
              onChange={(e) => patch({ monthly_budget_ntd: e.target.value === '' ? null : Number(e.target.value) })} />
            <TextField label={t('aiConfig.budgetAlert', '預算警示 %')} size="small" type="number" fullWidth
              value={cfg.budget_alert_percent ?? ''}
              onChange={(e) => patch({ budget_alert_percent: e.target.value === '' ? null : Number(e.target.value) })} />
          </Stack>
          <Box><Button variant="contained" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : t('common.save', '儲存')}</Button></Box>
        </Stack>
      </Paper>

      {/* 用量 */}
      {usage && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>{t('aiConfig.usage', '本月用量')}</Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2">
            {usage.period}：{usage.currency} {usage.total_cost}
            {usage.monthly_budget_ntd != null && ` / ${t('aiConfig.budget', '預算')} NT$${usage.monthly_budget_ntd}`}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TenantAiConfigPage;
